/**
 * Read the active portal slug from `/c/<slug>/...` in the URL.
 *
 * Why this exists: Win95Desktop is rendered both at the root
 * (`__root.tsx`) AND inside `/c/$slug/desktop.tsx`. Each runs its
 * own `<WindowProvider>`. The outer one mounts before the route
 * tree reaches the portal layout, so windows registered there
 * render OUTSIDE `<TenantBrandingProvider>`. Their
 * `useOptionalTenantBranding()` call returns null even though the
 * URL clearly says we're on a portal route.
 *
 * Consumers that need a portal slug to drive Convex queries should
 * use `useResolvedPortalSlug()` below, which combines the React
 * context (when present) with this URL fallback. The standalone
 * `readPortalSlugFromUrl()` is exported for non-component code
 * paths (e.g. MsalInstanceProvider's sign-in redirect handler).
 *
 * Pure-DOM read; safe in any browser context. Returns null on the
 * server, on non-portal routes, or if the URL doesn't match the
 * expected `/c/<slug>/...` shape.
 */
import { useLocation } from '@tanstack/react-router';
import { useOptionalTenantBranding } from './TenantBrandingContext';

export function readPortalSlugFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const segments = window.location.pathname.split('/');
  if (segments[1] !== 'c') return null;
  const slug = segments[2];
  return slug && slug.length > 0 ? slug : null;
}

function readSlugFromPath(pathname: string): string | null {
  const segments = pathname.split('/');
  if (segments[1] !== 'c') return null;
  const slug = segments[2];
  return slug && slug.length > 0 ? slug : null;
}

/**
 * React-aware portal slug. Prefers the React context (load-bearing
 * for branding metadata when present); falls back to the URL when
 * the context is shadowed (registered-window outer-WindowLayer
 * case). Re-runs on TanStack Router location changes so portal
 * switches re-key any Convex queries that consume this.
 *
 * Returns null when neither source has a slug (we're not on a
 * portal route).
 */
export function useResolvedPortalSlug(): string | null {
  const branding = useOptionalTenantBranding();
  // `useLocation()` re-renders us on route changes, which is the
  // signal we need; a raw `window.location.pathname` read would
  // miss SPA navigations.
  const { pathname } = useLocation();
  if (branding?.slug) return branding.slug;
  return readSlugFromPath(pathname);
}
